import { MODAL_NONE } from '@components/App/Modal/constants'
import { useProjectToolbar } from '@components/ArtBoard/ProjectToolbar'
import { useSelector } from 'react-redux'

import type { AppState } from '@/types'
import { allowEventTarget } from '@/utils/allowEventTarget'
import { Keys } from '../keyboard'

// Single keys for the action bar tools, the whiteboard way: C comments,
// N sticky note, E estimate, I image mode, S SEO mode (OCT-5195). A tool the
// bar hides or disables has no key either: the flags are the bar's own.
export const useOnToolKeyboardActions = () => {
  const modalType = useSelector<AppState, string>(({ ui }) => ui.modalType)

  const {
    actions,
    canEdit,
    commentsDisabled,
    estimateActive,
    estimateHidden,
    imageActive,
    imageHidden,
    modeLocked,
    seoActive,
    toolsDisabled,
  } = useProjectToolbar()

  // Comments and sticky notes belong to editing; the view toggles also work
  // inside their own mode, that is how the mode is left.
  const commentsAllowed = !modeLocked && !commentsDisabled
  const stickyAllowed = !modeLocked && canEdit && !toolsDisabled
  const estimateAllowed =
    (!modeLocked || estimateActive) && !estimateHidden && !toolsDisabled
  const imageAllowed =
    (!modeLocked || imageActive) && !imageHidden && !toolsDisabled
  // The SEO switch is the one control the bar never disables.
  const seoAllowed = !modeLocked || seoActive

  const run = (event: KeyboardEvent, action: () => void) => {
    event.preventDefault()
    action()
  }

  return (event: KeyboardEvent) => {
    if (modalType !== MODAL_NONE || !allowEventTarget(event)) {
      return
    }

    // plain letters only, otherwise C would eat Mod+C (Copy)
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return
    }

    switch (event.code) {
      case Keys.C: {
        if (commentsAllowed) {
          run(event, actions.onToggleComments)
        }

        return
      }

      case Keys.N: {
        if (stickyAllowed) {
          run(event, actions.onAddStickyNote)
        }

        return
      }

      case Keys.E: {
        if (estimateAllowed) {
          run(event, actions.onToggleEstimate)
        }

        return
      }

      case Keys.I: {
        if (imageAllowed) {
          run(event, actions.onToggleImageMode)
        }

        return
      }

      case Keys.S: {
        if (seoAllowed) {
          run(event, actions.onToggleSeoMode)
        }

        return
      }

      default: {
        return
      }
    }
  }
}
